import Modal from '@material-ui/core/Modal';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import EventIcon from '@material-ui/icons/Event';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import WidgetsIcon from '@material-ui/icons/Widgets';
import PeopleIcon from '@material-ui/icons/People';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@mainstage/core-plugin-api';
import { metricsApiRef } from '@agilelab/plugin-wb-metrics';
import { useQuery } from '@tanstack/react-query';
import { DefaultLogo, InfoIcon } from '@agilelab/plugin-wb-platform';
import { infoApiRef, InfoApi } from '@agilelab/plugin-wb-platform';
import { useTheme } from '@material-ui/core/styles';
import { getModuleOverrideEnabledFromConfig } from '@agilelab/plugin-wb-platform-common';
import { WbTooltip } from '../../../WbTooltip';
import { useId } from 'react';

const useStyle = makeStyles(theme => ({
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[24],
    width: '90%',
    maxWidth: 600,
    padding: theme.spacing(2, 3, 1.5, 3),
    outline: 'none',
    maxHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  modalRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    boxSizing: 'border-box',
    paddingLeft: 0,
    paddingRight: 0,
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  modalInfoRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    columnGap: 32,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  modalInfoCell: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '8px',
    },
  },
  infoLabel: {
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    fontSize: theme.typography.overline.fontSize,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  infoValue: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: theme.typography.body1.fontSize,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  loadingBox: {
    width: '100%',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: 24,
  },
  errorBox: {
    width: '100%',
    textAlign: 'center',
    color: theme.palette.error.main,
    padding: 24,
  },
  logoContainer: {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    verticalAlign: 'middle',
    marginRight: 4,
  },
  closeButton: {
    color: theme.palette.secondary.main,
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  divider: {
    width: '100%',
    margin: '16px 0',
  },
  contentContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      rowGap: 0,
    },
  },
}));

function formatUsersSummary(
  licensedRaw: unknown,
  active: number | undefined,
  isActiveLoading: boolean,
  activeUsersError: unknown,
): string {
  const activeStr = (() => {
    if (isActiveLoading) return 'Loading…';
    if (active !== undefined) return String(active);
    if (activeUsersError) return 'N/A';
    return '0';
  })();

  const licensedStr = (() => {
    if (licensedRaw === null || licensedRaw === undefined || licensedRaw === '')
      return 'N/A';
    const n = Number(licensedRaw);
    return Number.isFinite(n) ? String(n) : String(licensedRaw);
  })();

  return `${activeStr} of ${licensedStr}`;
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB');
}

export const AboutWitboostModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const classes = useStyle();
  const infoApi: InfoApi = useApi(infoApiRef);
  const identityApi = useApi(identityApiRef);
  const metricsApi = useApi(metricsApiRef);
  const configApi = useApi(configApiRef);

  const labelId = useId();

  const { data, isLoading, error } = useQuery({
    queryKey: ['about-witboost-info'],
    queryFn: async () => {
      const info = await infoApi.getInfo({});
      const modulesOverrideEnabled =
        getModuleOverrideEnabledFromConfig(configApi);
      const modules = modulesOverrideEnabled
        ? info.license.activeModules
        : info.license.modules;
      return {
        version: info.version || 'N/A',
        expiry: formatDate(info.license.expirationDate),
        modules: modules.join(', '),
        users: info.license.licensedUsers || 'Unlimited',
      };
    },
    enabled: open,
  });

  const {
    data: activeUsersData,
    isLoading: isActiveLoading,
    error: activeUsersError,
  } = useQuery<{ activeUsersOnControlPlane: number } | undefined>({
    queryKey: ['about-witboost-active-users'],
    queryFn: async () => {
      const { token } = await identityApi.getCredentials();
      return metricsApi.getActiveUsersOnControlPlane({ token });
    },
    enabled: open,
    retry: 1,
  });

  let content;
  if (isLoading) {
    content = <Box className={classes.loadingBox}>Loading...</Box>;
  } else if (error) {
    content = (
      <Box className={classes.errorBox}>
        Error: {error instanceof Error ? error.message : String(error)}
      </Box>
    );
  } else {
    content = (
      <>
        <Box className={classes.modalInfoRow}>
          <Box className={classes.modalInfoCell}>
            <span className={classes.infoLabel}>Version</span>
            <span className={classes.infoValue}>
              <span className={classes.iconContainer}>
                <VerifiedUserIcon
                  fontSize="small"
                  className={classes.icon}
                  htmlColor={theme.palette.secondary.main}
                />
                {data?.version || 'N/A'}
              </span>
            </span>
          </Box>
          <Box className={classes.modalInfoCell}>
            <span className={classes.infoLabel}>License Expiry</span>
            <span className={classes.infoValue}>
              <span className={classes.iconContainer}>
                <EventIcon
                  fontSize="small"
                  className={classes.icon}
                  htmlColor={theme.palette.secondary.main}
                />
                {data?.expiry || 'N/A'}
              </span>
            </span>
          </Box>
        </Box>
        <Box className={classes.modalInfoRow}>
          <Box className={classes.modalInfoCell}>
            <span className={classes.infoLabel}>
              Active Control Plane
              <WbTooltip title="Number of users who have used the Control Plane last month. This data is updated each 1st of the month.">
                <IconButton size="small" style={{ padding: 2, marginLeft: 4 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </WbTooltip>
            </span>
            <span className={classes.infoValue}>
              <span className={classes.iconContainer}>
                <PeopleIcon
                  fontSize="small"
                  className={classes.icon}
                  htmlColor={theme.palette.secondary.main}
                />
                {formatUsersSummary(
                  data?.users,
                  activeUsersData?.activeUsersOnControlPlane,
                  isActiveLoading,
                  activeUsersError,
                )}
              </span>
            </span>
          </Box>
          <Box className={classes.modalInfoCell}>
            <span className={classes.infoLabel}>Modules</span>
            <span className={classes.infoValue}>
              <span className={classes.iconContainer}>
                <WidgetsIcon
                  fontSize="small"
                  className={classes.icon}
                  htmlColor={theme.palette.secondary.main}
                />
                {data?.modules || 'N/A'}
              </span>
            </span>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby={labelId}>
        <Box className={classes.modalBox}>
          <Box className={classes.logoContainer}>
            <div id={labelId}>
              <DefaultLogo
                style={{ height: '40px' }}
                altText="About Witboost"
              />
            </div>
            <IconButton
              className={classes.closeButton}
              onClick={onClose}
              size="small"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider className={classes.divider} />
          <Box className={classes.contentContainer}>{content}</Box>
        </Box>
      </Modal>
    </>
  );
};
